// The shared "create a layout + instance from a config" tail: createLayout → createInstance →
// best-effort workspace PUT. Used directly for new dashboards and delegated to by
// migrateDashboardToInstance. Pure orchestration: the caller injects the create fns, so it's
// unit-testable without React.
import { createUuid, type Uuid } from "helpers/types/uuid"
import { warn } from "helpers/dev-logger"
import type { Layout } from "resources/layouts"
import type { DashboardInstance, CreateInstancePayload } from "resources/dashboard-instances"
import type { LayoutConfigV1, WorkspaceStateV1 } from "../contracts/layouts"
import { exportLayoutConfig } from "./export-layout-config"
import { serializeWorkspace, type WorkspaceSerializableState } from "./serialize-workspace"
import { createDefaultModules } from "../store/panels-slice"

// Synthetic key for projecting default modules into a config; never reaches the store.
const DEFAULTS_DASHBOARD_KEY = "new"

export interface InstantiateInstanceArgs {
  // Display name for both the layout and the instance.
  name: string
  // The portable layout to persist.
  config: LayoutConfigV1
  // The instance's initial autosave snapshot. Best-effort PUT after create (see below). When
  // omitted, the instance starts with no saved workspace and rebuilds from the layout on open.
  workspace?: WorkspaceStateV1
  createLayout: (name: string, config: LayoutConfigV1) => Promise<Layout>
  createInstance: (payload: CreateInstancePayload) => Promise<DashboardInstance>
  saveWorkspace: (instanceId: string, workspace: WorkspaceStateV1) => Promise<void>
}

/**
 * Create a layout + instance from `config`, returning the new instanceId + server-generated slug.
 *
 * Mirrors migrateDashboardToInstance's error contract: the two POSTs (layout, instance) propagate
 * their failures so the caller can toast and abort, but the trailing workspace PUT is best-effort —
 * the instance already exists and is routable, and the open-time autosave re-persists it, so a
 * failed PUT must NOT reject (rejecting would strand a created instance and risk a duplicate retry).
 */
export async function instantiateInstanceFromLayout({
  name,
  config,
  workspace,
  createLayout,
  createInstance,
  saveWorkspace,
}: InstantiateInstanceArgs): Promise<{ instanceId: Uuid, slug: string }> {
  const layout = await createLayout(name, config)

  const instanceId = createUuid()
  const created = await createInstance({ instanceId, name, layoutId: layout.id })

  if (workspace) {
    try {
      await saveWorkspace(instanceId, workspace)
    } catch (error) {
      warn("Instance workspace save failed; the open-time autosave will re-persist it", error)
    }
  }

  return { instanceId, slug: created.slug }
}

/**
 * Layout config + workspace for a fresh default dashboard from the CLIENT default modules — the
 * fallback when a user has no layout to instance from. Uses the same export/serialize path as the
 * lazy migration, so the result matches a migrated default dashboard for the SAME entitlement
 * (caller passes `hasThematicBetasContentSet` from `useHasThematicBetasContentSet()`, the same
 * source `useSyncPanelsPreferences` uses for a migrated dashboard's default panels). (APP-3284)
 */
export function buildDefaultDashboardConfig(
  hasThematicBetasContentSet = false,
): { config: LayoutConfigV1, workspace: WorkspaceStateV1 } {
  // Only `panels` carries data; empty slice maps let each panel resolve to its slice-default viewConfig.
  const state: WorkspaceSerializableState = {
    dashboard: {
      panels: {
        [DEFAULTS_DASHBOARD_KEY]: {
          modules: createDefaultModules(hasThematicBetasContentSet),
          isPreferencesLoaded: true,
        },
      },
      assetView: {},
      factorsView: {},
      graph: {},
      kpi: {},
      core: {},
      model: {},
      trade: {},
      optimization: {},
    },
  }

  const { config } = exportLayoutConfig(state, DEFAULTS_DASHBOARD_KEY, null)
  const workspace = serializeWorkspace(state, DEFAULTS_DASHBOARD_KEY, null)
  return { config, workspace }
}
